import { keepPreviousData, useQuery } from "@tanstack/react-query";
import i18n from "@/i18n";
import { api } from "@/lib/api";
import { UNPAGINATED_FETCH_SIZE } from "@/lib/paging";

/**
 * Here rather than under `features/wedstrijden`, because three screens with nothing to do with
 * beheer need to know which seizoen is running.
 */
export const seizoenenKey = ["seizoenen"] as const;

/**
 * One big page, since there have never been more than a handful of seizoenen. The list page
 * drives real paging through `useSeizoenenPaged` instead.
 */
export function useSeizoenen() {
  return useQuery({
    queryKey: seizoenenKey,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/seizoenen", {
        params: { query: { pageSize: UNPAGINATED_FETCH_SIZE } },
      });
      if (error) throw new Error(i18n.t("common:errors.seizoenen"));
      return data?.items ?? [];
    },
  });
}

const GESELECTEERD_KEY = "jbnl.geselecteerdSeizoenId";

/**
 * The seizoen a beheerder explicitly picked on the Seizoenen screen, for the rest of this
 * browser tab's session. Session-scoped rather than persisted: a beheerder switching to an
 * older seizoen for one sitting should not have next Tuesday's session silently reopen on it.
 */
export function leesGeselecteerdSeizoenId(): string | undefined {
  try {
    return sessionStorage.getItem(GESELECTEERD_KEY) ?? undefined;
  } catch {
    // Some browsers refuse storage access (private mode, a strict setting) - the pick is a
    // convenience, and falling back to the running seizoen is a fine second choice.
    return undefined;
  }
}

export function onthoudGeselecteerdSeizoenId(id: string): void {
  try {
    sessionStorage.setItem(GESELECTEERD_KEY, id);
  } catch {
    // See leesGeselecteerdSeizoenId - a pick that fails to save just does not stick.
  }
}

/**
 * The URL's seizoen, else the one last picked on the Seizoenen screen, else the running one.
 * Ploegen and Overzicht carry no `seizoen` param, so they start at the second step.
 */
export function kiesSeizoen<T extends { id: string }>(
  seizoenen: T[] | undefined,
  seizoenId?: string,
): T | undefined {
  return (
    seizoenen?.find((s) => s.id === seizoenId) ??
    seizoenen?.find((s) => s.id === leesGeselecteerdSeizoenId()) ??
    seizoenen?.[0]
  );
}

export const seizoenenPagedKey = (
  page: number | undefined,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
) => [...seizoenenKey, "paged", page, search, sortBy, sortDescending] as const;

/** The Seizoenen list page's own read: one page, searched and sorted on the server. */
export function useSeizoenenPaged(
  page: number | undefined,
  pageSize: number,
  search: string,
  sortBy: string | undefined,
  sortDescending: boolean,
) {
  return useQuery({
    queryKey: seizoenenPagedKey(page, search, sortBy, sortDescending),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/seizoenen", {
        params: { query: { page, pageSize, search: search || undefined, sortBy, sortDescending } },
      });
      if (error) throw new Error(i18n.t("common:errors.seizoenen"));
      return data;
    },
    // Each page is its own query key, so without this a click to a page never fetched before
    // clears `data` and swaps the table for a skeleton - unmounting the very pager button just
    // clicked, which loses focus and (in some browsers) scrolls the page back to the top. The
    // previous page's rows stay on screen, correct but stale, until the new ones arrive.
    placeholderData: keepPreviousData,
  });
}
